import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  Timestamp,
  collectionGroup,
  limit,
} from "firebase/firestore";
import { db } from "./init";
import { GiftCode, GiftCodeRedemption } from "@/types/giftCode";
import { convertTimestampToDate } from "./firestore";

/**
 * Firestore 禮物代碼數據類型（包含 Timestamp）
 */
interface FirestoreGiftCodeData {
  code?: string;
  coins?: number;
  description?: string;
  total_uses?: number;
  used_count?: number;
  status?: "active" | "expired" | "depleted";
  valid_from?: any; // Timestamp | Date | null
  valid_until?: any; // Timestamp | Date | null
  created_at?: any; // Timestamp | Date | null
  created_by?: string;
  usage_limit_per_user?: number;
  schemaVersion?: number;
}

/**
 * 轉換禮物代碼數據（處理 Timestamp）
 */
function convertGiftCodeData(data: FirestoreGiftCodeData): Omit<GiftCode, "id"> {
  return {
    code: data.code || "",
    coins: data.coins || 0,
    description: data.description,
    total_uses: data.total_uses || 0,
    used_count: data.used_count || 0,
    status: data.status || "active",
    valid_from: convertTimestampToDate(data.valid_from) as Date,
    valid_until: convertTimestampToDate(data.valid_until) as Date,
    created_at: convertTimestampToDate(data.created_at) as Date | undefined,
    created_by: data.created_by,
    usage_limit_per_user: data.usage_limit_per_user || 1,
    schemaVersion: data.schemaVersion || 1,
  };
}

/**
 * 計算禮物代碼狀態
 */
export function calculateGiftCodeStatus(code: GiftCode): "active" | "expired" | "depleted" {
  const now = new Date();
  if (code.used_count >= code.total_uses) {
    return "depleted";
  }
  if (code.valid_until && now > code.valid_until) {
    return "expired";
  }
  return "active";
}

/**
 * 獲取所有禮物代碼
 */
export async function getGiftCodes(): Promise<GiftCode[]> {
  try {
    const giftCodesRef = collection(db, "gift_codes");
    const q = query(giftCodesRef, orderBy("created_at", "desc"));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
      const data = convertGiftCodeData(doc.data());
      const giftCode: GiftCode = {
        id: doc.id,
        ...data,
        status: calculateGiftCodeStatus({ ...data, id: doc.id } as GiftCode),
      };
      return giftCode;
    });
  } catch (error) {
    console.error("Error getting gift codes:", error);
    throw error;
  }
}

/**
 * 獲取單個禮物代碼
 */
export async function getGiftCode(id: string): Promise<GiftCode | null> {
  try {
    const giftCodeRef = doc(db, "gift_codes", id);
    const giftCodeSnap = await getDoc(giftCodeRef);

    if (giftCodeSnap.exists()) {
      const data = convertGiftCodeData(giftCodeSnap.data());
      const giftCode: GiftCode = {
        id: giftCodeSnap.id,
        ...data,
        status: calculateGiftCodeStatus({ ...data, id: giftCodeSnap.id } as GiftCode),
      };
      return giftCode;
    }

    return null;
  } catch (error) {
    console.error("Error getting gift code:", error);
    throw error;
  }
}

/**
 * 創建禮物代碼
 */
export async function createGiftCode(
  data: Omit<GiftCode, "id" | "created_at" | "status">,
  createdBy: string
): Promise<string> {
  try {
    const giftCodesRef = collection(db, "gift_codes");
    
    // 驗證代碼格式
    if (!/^[A-Z0-9]{6,12}$/.test(data.code)) {
      throw new Error("代碼必須是6-12位大寫英數字");
    }

    // 確保代碼為大寫
    const normalizedCode = data.code.toUpperCase();

    // 檢查代碼是否已存在
    const existingQuery = query(giftCodesRef, where("code", "==", normalizedCode));
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      throw new Error("此代碼已存在");
    }

    const giftCodeData: any = {
      code: normalizedCode,
      coins: data.coins,
      description: data.description || "",
      total_uses: data.total_uses,
      used_count: 0,
      usage_limit_per_user: data.usage_limit_per_user,
      status: "active",
      valid_from: data.valid_from instanceof Date ? Timestamp.fromDate(data.valid_from) : data.valid_from,
      valid_until: data.valid_until instanceof Date ? Timestamp.fromDate(data.valid_until) : data.valid_until,
      created_at: Timestamp.now(),
      created_by: createdBy,
      schemaVersion: 1,
    };

    const docRef = await addDoc(giftCodesRef, giftCodeData);
    return docRef.id;
  } catch (error) {
    console.error("Error creating gift code:", error);
    throw error;
  }
}

/**
 * 更新禮物代碼
 */
export async function updateGiftCode(
  id: string,
  data: Partial<Omit<GiftCode, "id" | "created_at" | "status">>
): Promise<void> {
  try {
    const giftCodeRef = doc(db, "gift_codes", id);
    const updateData: any = {};

    // 如果更新代碼，需要驗證和檢查重複
    if (data.code !== undefined) {
      if (!/^[A-Z0-9]{6,12}$/.test(data.code)) {
        throw new Error("代碼必須是6-12位大寫英數字");
      }
      const normalizedCode = data.code.toUpperCase();
      
      // 檢查代碼是否已被其他文檔使用
      const existingQuery = query(collection(db, "gift_codes"), where("code", "==", normalizedCode));
      const existingSnapshot = await getDocs(existingQuery);
      const isUsedByOther = existingSnapshot.docs.some((doc) => doc.id !== id);
      if (isUsedByOther) {
        throw new Error("此代碼已被其他代碼使用");
      }
      
      updateData.code = normalizedCode;
    }

    if (data.coins !== undefined) updateData.coins = data.coins;
    if (data.description !== undefined) updateData.description = data.description;
    if (data.total_uses !== undefined) updateData.total_uses = data.total_uses;
    if (data.usage_limit_per_user !== undefined) updateData.usage_limit_per_user = data.usage_limit_per_user;
    
    if (data.valid_from instanceof Date) {
      updateData.valid_from = Timestamp.fromDate(data.valid_from);
    } else if (data.valid_from !== undefined) {
      updateData.valid_from = data.valid_from;
    }
    
    if (data.valid_until instanceof Date) {
      updateData.valid_until = Timestamp.fromDate(data.valid_until);
    } else if (data.valid_until !== undefined) {
      updateData.valid_until = data.valid_until;
    }

    // 重新計算狀態
    const currentDoc = await getDoc(giftCodeRef);
    if (currentDoc.exists()) {
      const currentData = convertGiftCodeData(currentDoc.data());
      const updatedData = { ...currentData, ...data };
      updateData.status = calculateGiftCodeStatus({ ...updatedData, id } as GiftCode);
    }

    await updateDoc(giftCodeRef, updateData);
  } catch (error) {
    console.error("Error updating gift code:", error);
    throw error;
  }
}

/**
 * 刪除禮物代碼
 */
export async function deleteGiftCode(id: string): Promise<void> {
  try {
    const giftCodeRef = doc(db, "gift_codes", id);
    await deleteDoc(giftCodeRef);
  } catch (error) {
    console.error("Error deleting gift code:", error);
    throw error;
  }
}

/**
 * 獲取特定代碼的兌換記錄
 * 使用 collectionGroup 查詢所有用戶的 redeem 子集合
 * 注意：此查詢需要 Firestore 複合索引 (redeem: code, type, redeemed_at)
 */
export async function getGiftCodeRedemptions(
  code: string,
  limitCount: number = 100
): Promise<GiftCodeRedemption[]> {
  try {
    // 使用 collectionGroup 查詢所有用戶的 redeem 子集合
    const redeemRef = collectionGroup(db, "redeem");
    const normalizedCode = code.toUpperCase();
    
    // 先查詢符合 code 和 type 的記錄
    const q = query(
      redeemRef,
      where("code", "==", normalizedCode),
      where("type", "==", "gift_code"),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);

    // 在記憶體中排序（因為 Firestore 的 collectionGroup 查詢限制）
    const redemptions = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        user_id: data.user_id || "",
        code: data.code || "",
        coins: data.coins || 0,
        redeemed_at: convertTimestampToDate(data.redeemed_at) as Date,
        type: "gift_code" as const,
      };
    });

    // 按時間降序排序
    return redemptions.sort((a, b) => b.redeemed_at.getTime() - a.redeemed_at.getTime());
  } catch (error) {
    console.error("Error getting gift code redemptions:", error);
    // 如果查詢失敗（可能是因為缺少索引），返回空陣列
    // 注意：collectionGroup 查詢需要建立複合索引
    // 如果沒有索引，這個查詢會失敗，但我們不應該阻止整個功能
    console.warn("Failed to query redemptions, may need to create Firestore index");
    return [];
  }
}

/**
 * 更新代碼使用次數（通常由 Functions 處理，但提供此函數以備不時之需）
 */
export async function incrementGiftCodeUsage(id: string): Promise<void> {
  try {
    const giftCodeRef = doc(db, "gift_codes", id);
    const currentDoc = await getDoc(giftCodeRef);
    
    if (!currentDoc.exists()) {
      throw new Error("Gift code not found");
    }

    const currentData = currentDoc.data();
    const newUsedCount = (currentData.used_count || 0) + 1;
    const newStatus = newUsedCount >= (currentData.total_uses || 0) ? "depleted" : "active";

    await updateDoc(giftCodeRef, {
      used_count: newUsedCount,
      status: newStatus,
    });
  } catch (error) {
    console.error("Error incrementing gift code usage:", error);
    throw error;
  }
}

